import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tabs, Tab, Spinner, Alert } from 'react-bootstrap';
import { emailTemplateService } from '../../services/api/emailTemplateService';
import { EmailTemplate } from '../../types/types';

interface SendEmailModalProps {
    show: boolean;
    onHide: () => void;
    candidateId: string;
    candidateName: string;
    candidateEmail: string;
    stageId?: string;
    onEmailSent?: () => void;
}

const SendEmailModal: React.FC<SendEmailModalProps> = ({
    show,
    onHide,
    candidateId,
    candidateName,
    candidateEmail,
    stageId,
    onEmailSent
}) => {
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [subjectEdited, setSubjectEdited] = useState<boolean>(false);
    const [customMessage, setCustomMessage] = useState<string>('');
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<string>('compose');

    // Load email templates when modal opens
    useEffect(() => {
        if (!show) return;

        // Ignore a response that arrives after the modal has closed or reopened
        let cancelled = false;

        const fetchTemplates = async () => {
            try {
                setLoading(true);
                setError(null);
                setSubjectEdited(false);
                setCustomMessage('');
                setPreviewHtml('');
                setActiveTab('compose');

                let templatesData: EmailTemplate[];

                if (stageId) {
                    // Get templates for specific stage
                    templatesData = await emailTemplateService.getTemplatesForStage(stageId);
                } else {
                    // Get all active templates
                    templatesData = await emailTemplateService.getActiveEmailTemplates();
                }

                if (cancelled) return;
                setTemplates(templatesData);

                // Default to the first template unless the previous selection is still available
                setSelectedTemplateId(prev =>
                    templatesData.some(t => t.id === prev) ? prev : (templatesData[0]?.id ?? ''));
            } catch (err) {
                if (cancelled) return;
                console.error('Failed to fetch email templates:', err);
                setError('Failed to load email templates');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchTemplates();
        return () => { cancelled = true; };
    }, [show, stageId]);

    // Pre-fill the subject from the selected template, unless the user has already edited it
    useEffect(() => {
        if (subjectEdited) return;
        setSubject(templates.find(t => t.id === selectedTemplateId)?.subject ?? '');
    }, [selectedTemplateId, templates, subjectEdited]);

    // Generate preview when template or custom message changes
    useEffect(() => {
        if (!show || !selectedTemplateId) return;

        const generatePreview = async () => {
            try {
                // The backend fills in the custom message using the same placeholder matching as sending
                const html = await emailTemplateService.previewEmail(selectedTemplateId, candidateId, customMessage);
                setPreviewHtml(html);
            } catch (err) {
                console.error('Failed to generate preview:', err);
                // Don't set error here to not disrupt the UI - the preview area will be empty
            }
        };

        const debounce = setTimeout(() => {
            generatePreview();
        }, 500);

        return () => clearTimeout(debounce);
    }, [show, selectedTemplateId, customMessage, candidateId]);

    const handleSendEmail = async () => {
        if (!selectedTemplateId) {
            setError('Please select an email template');
            return;
        }

        try {
            setSending(true);
            setError(null);

            const emailRequest = {
                to: candidateEmail,
                candidateId,
                templateId: selectedTemplateId,
                subject: subject.trim() || undefined,
                customContent: customMessage
            };

            await emailTemplateService.sendEmail(emailRequest);

            if (onEmailSent) onEmailSent();
            onHide();
        } catch (err) {
            console.error('Failed to send email:', err);
            setError('Failed to send email. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedTemplateId(e.target.value);
    };

    const handleCustomMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCustomMessage(e.target.value);
    };

    const handleTabChange = (tabKey: string | null) => {
        if (tabKey) setActiveTab(tabKey);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-envelope me-2"></i>
                    Send Email to {candidateName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

                <Tabs
                    activeKey={activeTab}
                    onSelect={handleTabChange}
                    className="mb-3"
                >
                    <Tab eventKey="compose" title="Compose">
                        {loading ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" />
                                <p className="mt-2">Loading templates...</p>
                            </div>
                        ) : (
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email Template</Form.Label>
                                    <Form.Select
                                        value={selectedTemplateId}
                                        onChange={handleTemplateChange}
                                        disabled={templates.length === 0}
                                    >
                                        {templates.length === 0 ? (
                                            <option value="">No templates available</option>
                                        ) : (
                                            <>
                                                <option value="">Select a template</option>
                                                {templates.map(template => (
                                                    <option key={template.id} value={template.id}>
                                                        {template.name}
                                                    </option>
                                                ))}
                                            </>
                                        )}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>To:</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={candidateEmail}
                                        disabled
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Subject</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={subject}
                                        onChange={(e) => {
                                            setSubject(e.target.value);
                                            setSubjectEdited(true);
                                        }}
                                        maxLength={200}
                                        disabled={!selectedTemplateId}
                                        placeholder="Email subject"
                                    />
                                    <Form.Text className="text-muted">
                                        Placeholders such as {'{{CandidateFirstName}}'} are filled in when sent.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Custom Message</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={customMessage}
                                        onChange={handleCustomMessageChange}
                                        placeholder="Enter any custom message to include in the email..."
                                    />
                                    <Form.Text className="text-muted">
                                            This will replace the {'{{CustomMessage}}'} placeholder in the email template.
                                    </Form.Text>
                                </Form.Group>
                            </Form>
                        )}
                    </Tab>

                    <Tab eventKey="preview" title="Preview">
                        <div className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <h5>Email Preview</h5>
                                <span className="text-muted small">
                                    {templates.find(t => t.id === selectedTemplateId)?.name || 'Select a template'}
                                </span>
                            </div>

                            {!selectedTemplateId ? (
                                <Alert variant="info">
                                    Please select an email template in the Compose tab to see a preview.
                                </Alert>
                            ) : (
                                <>
                                    <p className="mb-2">
                                        <span className="text-muted">Subject:</span>{' '}
                                        {subject || templates.find(t => t.id === selectedTemplateId)?.subject}
                                    </p>
                                    <div
                                        className="email-preview border p-3 rounded bg-light"
                                        style={{ minHeight: '300px', maxHeight: '400px', overflow: 'auto' }}
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    />
                                </>
                            )}
                        </div>
                    </Tab>
                </Tabs>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSendEmail}
                    disabled={sending || !selectedTemplateId}
                >
                    {sending ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-send me-1"></i>
                            Send Email
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SendEmailModal;